import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { uploadVideo } from "../utils/api";

function UploadPage() {
  useEffect(() => { document.title = "Upload · Pixrd"; }, []);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const navigate = useNavigate();
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragLeave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.type.startsWith("video/")) {
        setFile(droppedFile);
        setError(null);
      } else {
        setError("Please upload a video file.");
      }
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError("Please select a video file.");
      return;
    }

    setUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      const result = await uploadVideo(file, (progress) => {
        setUploadProgress(Math.round(progress));
      });
      navigate(`/results/${result.job_id}`);
    } catch (err) {
      setError("Upload failed: " + err.message);
      setUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-bg-primary pt-[80px] pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12 mt-12">
            <h1
              className="font-space text-[clamp(32px,4vw,48px)] mb-4"
              style={{
                color: "#b0b0b0",
                fontWeight: "700",
              }}
            >
              Turn Long Videos into Viral Highlights
            </h1>
            <p
              className="font-space text-[16px]"
              style={{
                color: "#777777",
                fontWeight: "500",
              }}
            >
              Upload your video and get clips in 60 seconds
            </p>
          </div>

          {/* Drag and Drop Area */}
          <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                className="border-2 border-dashed rounded-lg p-12 mb-6 text-center transition-all duration-200 cursor-pointer"
                style={{
                  borderColor: dragActive ? "#FF6B00" : "#888888",
                  backgroundColor: dragActive
                    ? "rgba(255, 107, 0, 0.05)"
                    : "transparent",
                }}
                onClick={() =>
                  !uploading && document.getElementById("file-input").click()
                }
              >
                {/* Upload Icon */}
                <div className="mb-4">
                  <svg
                    className="w-16 h-16 mx-auto transition-color duration-200"
                    style={{ color: dragActive ? "#FF6B00" : "#888888" }}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                </div>

                {/* Text */}
                <p
                  className="font-space text-[18px] mb-2"
                  style={{
                    color: "#888888",
                    fontWeight: "500",
                  }}
                >
                  {file ? file.name : "Drag & drop your video here"}
                </p>
                <p
                  className="font-space text-[14px] mb-4"
                  style={{
                    color: "#777777",
                    fontWeight: "500",
                  }}
                >
                  or click to browse
                </p>
                <p
                  className="font-space text-[12px]"
                  style={{
                    color: "#777777",
                    fontWeight: "500",
                  }}
                >
                  MP4, MOV, AVI • MAX 1 GB
                </p>

                {/* Hidden File Input */}
                <input
                  id="file-input"
                  type="file"
                  accept="video/*"
                  onChange={handleFileChange}
                  disabled={uploading}
                  className="hidden"
                />
              </div>

              {/* File Info */}
              {file && (
                <div
                  className="font-space text-[14px] text-center mb-6 p-4 border rounded"
                  style={{
                    color: "#b0b0b0",
                    borderColor: "#888888",
                    fontWeight: "500",
                  }}
                >
                  selected:{" "}
                  <span style={{ color: "#FF6B00" }}>{file.name}</span> (
                  {(file.size / 1024 / 1024).toFixed(2)} MB)
                </div>
              )}

              {/* Upload progress Bar */}
              {uploading && (
               <div className="mb-6">
                 <div className="mb-3 flex justify-end">
                    <p className="font-space text-[14px]"
                      style={{color: "#FF6B00", fontWeight: "600"}}>
                        {uploadProgress}%
                    </p>
                  </div>
                  <div className="w-full h-2 rounded-full overflow-hidden"
                    style={{backgroundColor: "#333333"}}>
                      <div style={{width: `${uploadProgress}%`, backgroundColor: "#FF6B00", height: "100%", transition: "width 0.3s ease"}} />
                  </div>
                </div>
              )}

              {/* Upload Button */}
              <button
                onClick={handleUpload}
                disabled={!file || uploading}
                className="w-full font-space text-[14px] tracking-[3px] uppercase py-4 transition-all duration-200 rounded"
                style={{
                  backgroundColor: !file || uploading ? "#666666" : "#FF6B00",
                  color: "#000000",
                  fontWeight: "700",
                  cursor: !file || uploading ? "not-allowed" : "pointer",
                  opacity: !file || uploading ? 0.6 : 1,
                }}
                onMouseEnter={(e) => {
                  if (file && !uploading)
                    e.target.style.backgroundColor = "#FF9A3C";
                }}
                onMouseLeave={(e) => {
                  if (file && !uploading)
                    e.target.style.backgroundColor = "#FF6B00";
                }}
              >
                {uploading ? "Uploading..." : "Upload & Process"}
              </button>

              {/* Error Message */}
              {error && (
                <p
                  className="font-space text-[14px] text-center mt-6 p-4 border rounded"
                  style={{
                    color: "#FF6B00",
                    borderColor: "#FF6B00",
                    fontWeight: "600",
                  }}
                >
                  {error}
                </p>
              )}
        </div>
      </div>

      <Footer />
    </>
  );
}

export default UploadPage;
